import * as Sentry from '@sentry/browser';
import { AIChatbotEntryPoint } from 'Analytics/ai-chatbot-events';
import 'Askmedi/components/vm-askmedi-hero/vm-askmedi-hero';
import { ApiFetchError, buildURL, fetchRequest } from 'Utils/api-fetch-request';
import ElementVisibility from 'Utils/element-visibility';
import createVideoItem from 'Utils/video-item';

const __CONTEXT__ = {
    longThumbnail: window.__VUMEDI_HOMEFEED_CONTEXT__.static.longThumbnail,
    siteSlug: window.__VUMEDI_HOMEFEED_CONTEXT__.siteSlug,
};

class LoadingController {
    constructor(parentSelector) {
        this.loadingRoot = document.querySelector(`${parentSelector} .js_loading`);
    }

    stop() {
        this.loadingRoot.remove();
    }

    fail(message) {
        if (this.loadingRoot) {
            this.loadingRoot.classList.add('homefeed-page__loading--message');
            this.loadingRoot.innerText = message ?? 'Loading failed.';
        }
    }
}

class SectionHandler {
    constructor(rootSelector, requestBase, params = {}, videoItemOptionsLong = {}) {
        this.params = params;
        this.requestBase = requestBase;
        this.videoItemOptionsLong = videoItemOptionsLong;

        this.loadingController = new LoadingController(rootSelector);
        this.root = document.querySelector(rootSelector);
    }

    getVideos() {
        return new Promise((resolve, reject) => {
            fetchRequest(buildURL(this.requestBase, this.params))
                .then((data) => {
                    if (!data?.results?.length) {
                        reject(new Error(`${this.requestBase}: empty payload`));
                        return;
                    }

                    let hasCampaign = false;

                    const videos = data.results.map((el, i) => {
                        const target = el.content ? el.content : el;

                        if (el.viewed_time && !target.viewed_time) {
                            target.viewed_time = el.viewed_time;
                        }

                        if (el.campaign_data) {
                            target.campaign_data = el.campaign_data;
                            hasCampaign = true;
                        }

                        if (this.requestBase === '/api/recommended/') {
                            target.urlParams = {
                                source: 'homefeed',
                                idx: i + 1,
                            };
                        }

                        return target;
                    });

                    if (hasCampaign && videos.length > this.params.limit) {
                        videos.pop();
                    }

                    resolve(videos);
                })
                .catch((error) => reject(error));
        });
    }

    load() {
        this.getVideos()
            .then((videos) => {
                this.loadingController.stop();
                this.render(videos);
                this.handleShowMore();
            })
            .catch((error) => {
                let message;

                if (error.message.includes('empty payload')) {
                    message = 'There are no available videos.';
                } else if (error instanceof ApiFetchError && error.status === 401) {
                    message = 'Please log in to proceed.';
                } else {
                    Sentry.captureException(error);
                    message = 'Oops, something went wrong.';
                }

                this.loadingController.fail(message);
            });
    }

    render(videos) {
        const list = this.root.querySelector('.homefeed-page__list-items');

        for (const video of videos) {
            const longOptions = {
                thumbnailPlaceholder: __CONTEXT__.longThumbnail,
                includeFeaturing: true,
                includeThumbups: false,
                showAuthor: true,
                showFeatured: !!video.campaign_data,
            };

            const videoItem = createVideoItem('long', video, {
                long: Object.assign(longOptions, this.videoItemOptionsLong),
            });

            if (videoItem) {
                const videoContent = document.createElement('div');
                videoContent.classList.add('homefeed-page__video');
                videoContent.appendChild(videoItem);
                list.appendChild(videoContent);
            }
        }

        this.root.classList.remove('hidden');
    }

    handleShowMore() {
        const list = this.root.querySelector('.homefeed-page__list-items');
        const showMoreButton = this.root.querySelector('.js_showMore');
        const videoItems = list.querySelectorAll('.homefeed-page__video');

        let expanded = false;

        // --cards-per-row (homefeed.scss) is the single source of truth for
        // the per-breakpoint column count; read it instead of keeping a second
        // copy of the breakpoints here.
        const getRowCount = () => parseInt(getComputedStyle(list).getPropertyValue('--cards-per-row'), 10);

        const update = () => {
            const visibleCount = getRowCount() * (expanded ? 2 : 1);

            videoItems.forEach((el, i) => el.classList.toggle('hidden', i >= visibleCount));
            showMoreButton.classList.toggle('vm-button--hidden', expanded || videoItems.length <= visibleCount);
        };

        showMoreButton.addEventListener('click', (ev) => {
            ev.preventDefault();
            expanded = true;
            update();
        });

        update();
        new ResizeObserver(update).observe(list);
    }
}

class NewsHandler {
    constructor(rootSelector) {
        this.hasNext = true;
        this.infiniteScroll = null;
        this.isLoading = false;
        this.limit = 12;
        this.loadingController = new LoadingController(rootSelector);
        this.offset = 0;
        this.root = document.querySelector(rootSelector);
    }

    setup() {
        const loadingIcon = this.root.querySelector('.js_loading');
        this.infiniteScroll = new ElementVisibility(loadingIcon, () => {
            if (!this.isLoading) {
                this.loadNext();
            }
        });
        this.infiniteScroll.observe();
    }

    getVideos() {
        return new Promise((resolve, reject) => {
            const url = `/api/videos/?site=${__CONTEXT__.siteSlug}&limit=${this.limit}&offset=${this.offset}&exclude_promoted=true&ordering=-created`;

            this.isLoading = true;

            fetchRequest(url)
                .then((data) => {
                    if (!data?.results?.length) {
                        reject(new Error('/api/videos/: empty payload'));
                        return;
                    }

                    this.hasNext = !!data.next;

                    const videos = data.results;

                    if (!videos.length) {
                        reject(new Error('/api/videos/: no video entries'));
                        return;
                    }

                    this.offset += this.limit;

                    // If video from a campaign is returned, total number of response videos is offset + 1
                    if (data.results[0].campaign_data) {
                        videos[0].campaign_data = data.results[0].campaign_data;
                        videos.pop();
                        --this.offset;
                    }

                    resolve(videos);
                })
                .catch((error) => reject(error))
                .finally(() => {
                    this.isLoading = false;
                });
        });
    }

    render(videos) {
        const target = document.querySelector('.js_newsVideos');
        const listTarget = target.querySelector('.homefeed-page__list-items');

        for (const video of videos) {
            const videoItem = createVideoItem('long', video, {
                long: {
                    thumbnailPlaceholder: __CONTEXT__.longThumbnail,
                    includeFeaturing: true,
                    includeThumbups: false,
                    showAuthor: true,
                    showFeatured: !!video.campaign_data,
                },
            });

            if (videoItem) {
                const videoContent = document.createElement('div');
                videoContent.classList.add('homefeed-page__video');
                videoContent.appendChild(videoItem);
                listTarget.appendChild(videoContent);
            }
        }
    }

    loadNext() {
        this.getVideos()
            .then((videos) => {
                this.render(videos);

                if (!this.hasNext) {
                    this.loadingController.stop();
                    this.infiniteScroll.remove();
                }
            })
            .catch((error) => {
                let message;

                if (error instanceof ApiFetchError && error.status === 401) {
                    message = 'Please log in to proceed.';
                } else {
                    Sentry.captureException(error);
                    message = 'Oops, something went wrong.';
                }

                this.loadingController.fail(message);
            });
    }
}

function registerEvents() {
    document.querySelector('.js_recommendedVideos .js_showMore')?.addEventListener('click', () => {
        window.eventTracker.trackHomefeedShowMore();
    });
}

function setupAskmediHero() {
    const hero = document.querySelector('vm-askmedi-hero');
    if (!hero) return;

    hero.addEventListener('vm-send', (e) => {
        const { message, source } = e.detail;
        // entry_point records the homepage-hero origin (separate from question_source).
        const entryPoint =
            source === 'predefined' ? AIChatbotEntryPoint.HOMEPAGE_PREDEFINED : AIChatbotEntryPoint.HOMEPAGE_CUSTOM;
        const params = new URLSearchParams({ q: message, source, entry_point: entryPoint });
        window.location.assign(`/askmedi/?${params.toString()}`);
    });
}

function main() {
    const recommended = new SectionHandler('.js_recommendedVideos', '/api/recommended/', {
        limit: 8,
        exclude_promoted: true,
        show_campaigns: true,
        placement: 'heartbeat',
    });
    recommended.load();

    const continueWatching = new SectionHandler(
        '.js_continueWatching',
        '/api/v1/continue-watching/',
        {},
        {
            showViewedProgress: true,
            addURLTimestamp: true,
        },
    );
    continueWatching.load();

    const news = new NewsHandler('.js_newsVideos');
    news.setup();
    news.loadNext();

    setupAskmediHero();
    registerEvents();
}

window.addEventListener('DOMContentLoaded', main);
